import type { Role } from "@wwrp/shared";
import { Badge } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { roleMappingsApi } from "../api/role-mappings.js";

export const ROLE_LABEL: Record<Role, string> = {
  applicant: "Applicant",
  recruit: "Recruit",
  member: "Member",
  staff_in_training: "Staff in Training",
  staff: "Staff",
  senior_staff: "Senior Staff",
  junior_administration: "Junior Administration",
  administration: "Administration",
  senior_administration: "Senior Administration",
  head_administration: "Community Leadership",
};

/**
 * Discord role hex colours mirrored from the WWRP guild so the hub badges
 * read the same as the in-Discord role chips. Roles whose Discord
 * counterpart is black (Community Leadership) get bespoke "prestige"
 * hexes that read as top-of-ladder on the OLED dark theme without going
 * invisible — black-on-black would just disappear here.
 */
export const FALLBACK_ROLE_COLOR = "var(--mantine-color-gray-6)";

export function useRoleColors(): Partial<Record<Role, string>> {
  const { data } = useQuery({
    queryKey: ["role-mappings", "colors"],
    queryFn: () => roleMappingsApi.colors(),
    staleTime: 60_000,
  });
  return data ?? {};
}

export function useRoleColor(role: Role): string {
  const colors = useRoleColors();
  return colors[role] ?? FALLBACK_ROLE_COLOR;
}

export function RoleBadge({ role }: { role: Role }) {
  const resolvedColor = useRoleColor(role);
  return (
    <Badge className="role-badge" color={resolvedColor} variant="light" radius="sm" size="sm">
      {ROLE_LABEL[role] ?? role}
    </Badge>
  );
}
